import { useState } from 'react';
import { ChevronDown, History, LayoutTemplate, Save, Star, ListTree } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import type { BreakdownDocumentContents, BreakdownSlotKey } from '@jobos/shared';
import { useBreakdownDocuments } from '@/hooks/useBreakdownDocuments';
import { SaveAsDialog } from './SaveAsDialog';
import { SaveAsTemplateDialog } from './SaveAsTemplateDialog';
import { SetActiveConfirm } from './SetActiveConfirm';

export interface VersionSwitcherProps {
  jobId: string;
  // Slot whose documents to list — scopes the query cache per slot.
  slotKey: BreakdownSlotKey;
  // Currently-open document id (null when "All versions" is shown).
  documentId: string | null;
  // Navigate to ?docId=<id>. Clearing (null) goes to the document-list view.
  onNavigate: (docId: string | null) => void;
  disabled?: boolean;
  // Job project type — seeds the save-as-template dialog's type picker.
  jobProjectType?: string | null;
  // Serializes the live editor state for the template save, so the template
  // captures what is on screen rather than the slot's Active document. Omitted
  // by callers with no full-breakdown editor state (the carpet single-category
  // view), which hides the save-as-template entry entirely.
  getContents?: () => BreakdownDocumentContents;
}

// In-editor switcher that replaces VersionDropdown. Shows the current doc's
// name (with ★ when it's the Active one for the slot) and a dropdown of the
// other docs for quick switching. The footer holds escape hatches: open the
// full list, Save As, and Set as Active (only when the open doc isn't already
// Active — the list view also exposes this on every row).
export function VersionSwitcher({
  jobId,
  slotKey,
  documentId,
  onNavigate,
  disabled = false,
  jobProjectType,
  getContents,
}: VersionSwitcherProps) {
  const { data: documents } = useBreakdownDocuments(jobId, slotKey);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [setActiveOpen, setSetActiveOpen] = useState(false);

  const current = documents?.find((d) => d.id === documentId) ?? null;
  const others = documents?.filter((d) => d.id !== documentId) ?? [];
  const canSetActive = !!current && !current.isActive;

  const triggerLabel = current?.name ?? 'All versions';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <History className="h-4 w-4" />
            <span className="truncate max-w-[200px]">{triggerLabel}</span>
            {current?.isActive && (
              <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
            )}
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          {others.length === 0 ? (
            <div className="px-2 py-2 text-xs text-gray-500">No other versions</div>
          ) : (
            others.map((d) => (
              <DropdownMenuItem key={d.id} onClick={() => onNavigate(d.id)}>
                <div className="flex items-center gap-2 w-full">
                  {d.isActive ? (
                    <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
                  ) : (
                    <span className="w-3.5" />
                  )}
                  <span className="truncate flex-1 text-sm">{d.name}</span>
                  {d.tag && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                      {d.tag}
                    </span>
                  )}
                </div>
              </DropdownMenuItem>
            ))
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onNavigate(null)}>
            <ListTree className="h-4 w-4 mr-2" />
            All versions...
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setSaveAsOpen(true)}>
            <Save className="h-4 w-4 mr-2" />
            Save as...
          </DropdownMenuItem>
          {getContents && (
            <DropdownMenuItem onClick={() => setSaveTemplateOpen(true)}>
              <LayoutTemplate className="h-4 w-4 mr-2" />
              Save as job type template...
            </DropdownMenuItem>
          )}
          {canSetActive && (
            <DropdownMenuItem onClick={() => setSetActiveOpen(true)}>
              <Star className="h-4 w-4 mr-2" />
              Set as Active
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <SaveAsDialog
        jobId={jobId}
        source={current ? { id: current.id, name: current.name } : null}
        open={saveAsOpen}
        onOpenChange={setSaveAsOpen}
        onCreated={(doc) => onNavigate(doc.id)}
      />

      {getContents && (
        <SaveAsTemplateDialog
          open={saveTemplateOpen}
          onOpenChange={setSaveTemplateOpen}
          jobId={jobId}
          jobProjectType={jobProjectType ?? ''}
          getContents={getContents}
        />
      )}

      <SetActiveConfirm
        jobId={jobId}
        document={current ? { id: current.id, name: current.name } : null}
        open={setActiveOpen}
        onOpenChange={setSetActiveOpen}
      />
    </>
  );
}
